import { createContext, useState } from 'react'

import { useAxios } from '../../utilities/Requests/useAxios'

export interface ResourcesContextInterface {
  rootResourceFolders: rootResourceFolder[] | null
  getRootResourceFolders: () => Promise<any>
  getFolderContentById: (folderId: number) => Promise<any>
  createResourceFolder: (
    newFolderName: string,
    parentFolderId?: number
  ) => Promise<any>
  createResourceFile: (
    fileName: string,
    folderId: number,
    fileStorageKey: string
  ) => Promise<any>
  deleteResourceFile: (fileId: number, folderId: number) => Promise<any>
  deleteResourceFolder: (folderId: number) => Promise<any>
  updateResourceFolderName: (
    folderId: number,
    newFolderName: string
  ) => Promise<any>
  updateResourceFile: (request: {
    fileId: number
    folderId: number
    newFileName: string
    newStorageKey: string
  }) => Promise<any>
  markFileVisibleToPatient: (request: {
    fileId: number
    patientIds: number[]
  }) => Promise<any>
  unassignFileFromPatients: (request: {
    fileId: number
    patientIds: number[]
  }) => Promise<any>
  getPatientsAssignedToFile: (fileId: number) => Promise<any>
}

export type rootResourceFolder = {
  id: number
  name: string
  parentFolderId: number
  type: 'folder'
}

export type ResourceFilePatient = {
  patientId: number
  preferredName: string
  photoUrl: string | null
  mrn: string
  deactivationDate: Date
  isActive: boolean
  assignedBy: string
}

export const ResourcesContext = createContext<ResourcesContextInterface | null>(
  null
)

export const ResourcesProvider = ({ children }: any) => {
  const { fetch } = useAxios()

  const [rootResourceFolders, setRootResourceFolders] = useState<
    rootResourceFolder[] | null
  >(null)

  const getRootResourceFolders = async () => {
    const { data, error } = await fetch({
      path: 'Resource/GetRootResourceFolders',
    })

    if (data) {
      data.forEach((folder: rootResourceFolder) => {
        folder.type = 'folder'
      })
      setRootResourceFolders([...data])
      return data
    } else {
      console.warn(`getRootResourceFolder-error: ${error}`)
    }
  }

  const getFolderContentById = async (folderId: number) => {
    const { data, error } = await fetch({
      path: `Resource/GetFolderContentById?folderId=${folderId}`,
    })

    if (data) {
      return data
    } else {
      console.warn(`getFolderContentById-error: ${error}`)
    }
  }

  const createResourceFolder = async (
    newFolderName: string,
    parentFolderId?: number
  ) => {
    if (!newFolderName.trim()) {
      alert('Please enter a valid folder name.')
      return
    }

    const { data, error } = await fetch({
      path: `Resource/CreateResourceFolder?folderName=${newFolderName}${
        parentFolderId ? `&parentFolderId=${parentFolderId}` : ''
      }`,
      methodType: 'POST',
    })

    if (data && rootResourceFolders) {
      rootResourceFolders.push(data)
      setRootResourceFolders([...rootResourceFolders])
      return data
    } else {
      console.warn(`CreateResourceFolder-error: ${error}`)
    }
  }

  const createResourceFile = async (
    fileName: string,
    folderId: number,
    fileStorageKey: string
  ) => {
    if (!fileName.trim()) {
      alert('Please enter a valid file name.')
      return
    }

    const { data, error } = await fetch({
      path: `Resource/CreateResourceFile?fileName=${fileName}&folderId=${folderId}&fileStorageKey=${fileStorageKey}`,
      methodType: 'POST',
    })

    if (data && rootResourceFolders) {
      rootResourceFolders.push(data)
      setRootResourceFolders({ ...rootResourceFolders })
      return data
    } else {
      console.warn(`createResourceFile-error: ${error}`)
    }
  }

  const deleteResourceFile = async (fileId: number, folderId: number) => {
    const { data, error } = await fetch({
      path: `Resource/DeleteResourceFile?fileId=${fileId}&folderId=${folderId}`,
      methodType: 'DELETE',
    })

    if (data) {
      return data
    } else {
      console.warn(`deleteResourceFile-error: ${error}`)
    }
  }

  const deleteResourceFolder = async (folderId: number) => {
    const { data, error } = await fetch({
      path: `Resource/DeleteResourceFolder?folderId=${folderId}`,
      methodType: 'DELETE',
    })

    if (data) {
      return data
    } else {
      console.warn(`deleteResourceFolder-error: ${error}`)
    }
  }

  const updateResourceFolderName = async (
    folderId: number,
    newFolderName: string
  ) => {
    const { data, error } = await fetch({
      path: `Resource/UpdateResourceFolderName?folderId=${folderId}&newFolderName=${newFolderName}`,
      methodType: 'PUT',
    })

    if (data) {
      return data
    } else {
      console.warn(`UpdateResourceFolderName-error: ${error}`)
    }
  }

  const updateResourceFile = async (request: {
    fileId: number
    folderId: number
    newFileName: string
    newStorageKey: string
  }) => {
    const { data, error } = await fetch({
      path: `Resource/UpdateResourceFile`,
      methodType: 'PUT',
      body: request,
    })

    if (data) {
      return data
    } else {
      console.warn(`UpdateResourceFile-error: ${error}`)
    }
  }

  const markFileVisibleToPatient = async (request: {
    fileId: number
    patientIds: number[]
  }) => {
    const { data, error } = await fetch({
      path: `Resource/MarkFileVisibleToPatient`,
      methodType: 'POST',
      body: request,
    })

    if (data) {
      return data
    } else {
      console.warn(`markFileVisibleToPatient-error: ${error}`)
    }
  }

  const unassignFileFromPatients = async (request: {
    fileId: number
    patientIds: number[]
  }) => {
    const { data, error } = await fetch({
      path: `Resource/UnassignFileFromPatients`,
      methodType: 'POST',
      body: request,
    })

    if (data) {
      return data
    } else {
      console.warn(`unassignFileFromPatients-error: ${error}`)
    }
  }

  const getPatientsAssignedToFile = async (fileId: number) => {
    const { data, error } = await fetch({
      path: `Resource/GetPatientsAssignedToFile?fileId=${fileId}`,
    })

    if (data) {
      return data
    } else {
      console.warn(`getPatientsAssignedToFile-error: ${error}`)
    }
  }

  return (
    <ResourcesContext.Provider
      value={{
        rootResourceFolders,
        getRootResourceFolders,
        getFolderContentById,
        createResourceFolder,
        createResourceFile,
        deleteResourceFile,
        deleteResourceFolder,
        updateResourceFolderName,
        updateResourceFile,
        markFileVisibleToPatient,
        unassignFileFromPatients,
        getPatientsAssignedToFile,
      }}
    >
      {children}
    </ResourcesContext.Provider>
  )
}
